<script setup lang="ts">
import { ref, watch } from 'vue';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import type { UpdateRobotGroup } from '@/models/Robot';
import type { Group } from '@/models/Group';
import { updateRobotGroup } from '@/api/RobotsApi';
import { loadUserGroups } from '@/api/GroupApi';

const props = defineProps<{ isOpen: boolean; serialNumber: string }>();
const emit = defineEmits(['update:isOpen', 'robot-moved']);

const isOpen = ref(props.isOpen);
const robotGroupUpdate = ref<UpdateRobotGroup>({
  serialNumber: props.serialNumber,
  groupUuid: '',
});
const updateError = ref('');
const loadingSpinnerStore = useLoadingSpinnerStore();
const groups = ref<Group[]>([]);

const cancel = () => {
  isOpen.value = false;
  emit('update:isOpen', false);
};

const updateGroup = async () => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    await updateRobotGroup(robotGroupUpdate.value);
    emit('robot-moved', true);
    cancel();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    updateError.value = err.message || 'Error updating robot';
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

async function fetchGroups() {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    groups.value = await loadUserGroups();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    updateError.value = err.message || 'Error fetching groups';
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

watch(
  () => props.isOpen,
  async (newVal) => {
    isOpen.value = newVal;
    updateError.value = '';
    robotGroupUpdate.value.groupUuid = '';
    if (newVal && props.serialNumber) {
      robotGroupUpdate.value.serialNumber = props.serialNumber;
      await fetchGroups();
    }
  },
);
</script>

<template>
  <v-dialog v-model="isOpen" max-width="600" @click:outside="cancel">
    <v-card>
      <v-card-title>{{ $gettext('Move Robot to another group') }}</v-card-title>
      <v-card-text>
        <p>
          <strong>{{ $gettext('Robot') }}:</strong>
          {{ robotGroupUpdate.serialNumber }}
        </p>
        <br />
        <v-form>
          <v-autocomplete
            v-model="robotGroupUpdate.groupUuid"
            :items="groups"
            item-title="name"
            item-value="uuid"
            :label="$gettext('Group')"
          ></v-autocomplete>
        </v-form>
        <v-alert v-if="updateError" class="mx-4" type="error" outlined>
          {{ updateError }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-btn @click="cancel">{{ $gettext('Close') }}</v-btn>
        <v-spacer />
        <v-btn color="primary" @click="updateGroup">{{
          $gettext('Move')
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
